import { useMemo } from 'react'
import { useAppSelector } from 'hooks/reduxHooks'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { OptimizelyExperiments } from 'constants/optimizely'
import { getFunnelFeatures, getFunnelEvaluationMetadata, FunnelEvaluationMetadata, getFunnelOptimiserRuleFlagKey } from 'checkout/selectors/funnel'

export function useFunnelFeature(feature: keyof App.FunnelFeatureToggles): boolean {
  return useAppSelector(getFunnelFeatures)[feature]
}

/**
 * Hook to get the funnel evaluation metadata (ruleId, ruleName, outcome).
 * Useful for debugging, analytics, or displaying which rule was applied.
 */
export function useFunnelEvaluationMetadata(): FunnelEvaluationMetadata {
  return useAppSelector(getFunnelEvaluationMetadata)
}

/**
 * Triggers the Optimizely experiment for a matched funnel rule.
 * This ensures activation only happens for users who meet the rule criteria.
 */
export function useFunnelOptimiserExperimentTrigger(): void {
  const { ruleId, outcome } = useFunnelEvaluationMetadata()
  const experimentKey = useMemo(() => getFunnelOptimiserRuleFlagKey(ruleId), [ruleId])
  const canTrigger = outcome === 'matched' && !!experimentKey
  const experimentId = useMemo(
    () => (experimentKey ?? '') as OptimizelyExperiments,
    [experimentKey],
  )

  useOptimizelyExperiment(experimentId, canTrigger)
}
